'use client'

import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import Link from 'next/link'
import { useRouter, usePathname } from 'next/navigation'
import { useSession, signIn, signOut } from 'next-auth/react'
import { LanguageSwitcher } from '@/components/layout/language-switcher'
import { HeaderQuickIcons } from '@/components/layout/header-quick-icons'
import { OnlineCounter } from '@/components/layout/online-counter'
import { Turnstile } from '@/components/security/turnstile'
import { CountryCombobox } from '@/components/ui/country-combobox'
import { useTranslation } from '@/i18n/client'
import { FglLogoSvg } from '@/components/brand/fgl-logo-svg'
import { TwoFactorOnboardingModal, useTwoFactorOnboardingTrigger } from '@/components/auth/two-factor-onboarding-modal'
import { LaunchCountdown } from '@/components/launch/launch-countdown'
import { RecentFlightsTicker } from '@/components/layout/recent-flights-ticker'
import {
  Newspaper, Trophy, Sparkles, MapPin, Cloud, Box, Users, Rocket, Heart, HelpCircle, Activity, LogIn, Mic, Plane,
} from 'lucide-react'

// 12 Farbschemata, benannt nach Segelflug-Stimmungen, sortiert hell -> dunkel.
type Theme =
  | 'start' | 'schwacheraufwind' | 'aufkurs' | 'aussenlandung'
  | 'gleiten' | 'ueberland' | 'ruppig' | 'starkethermik'
  | 'endanflug' | 'abschlussbier' | 'thermik' | 'speed'

// Migration: alte Keys -> neue Keys, damit localStorage von Bestandsnutzern weiter greift.
const LEGACY_THEME_MAP: Record<string, Theme> = {
  cumulus: 'start',
  morgenrot: 'schwacheraufwind',
  himmelsstuermer: 'aufkurs',
  goodlandings: 'aussenlandung',
  bluesky: 'gleiten',
  suedafrika: 'ueberland',
  hangwind: 'ruppig',
  abendbart: 'starkethermik',
  seewind: 'endanflug',
  waldkante: 'abschlussbier',
  thermik: 'thermik',
  nachtflug: 'speed',
}

// SVG-Icons inline (16x16) — der Accent wird via currentColor eingefaerbt.
const ThemeIcon = ({ theme }: { theme: Theme }) => {
  const s = 16
  const icons: Record<Theme, React.ReactNode> = {
    // Start: Segler vor Startbahn (Pfeil nach oben)
    start: (
      <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M8 14V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><path d="M5 7l3-3 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M2 14h12" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity=".6"/></svg>
    ),
    // Schwacher Aufwind: leichte Spirale
    schwacheraufwind: (
      <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M8 14c-3 0-4-2-4-4s2-3 4-3 3 1 3 2.5S9.5 11 8 11s-1.5-1.5 0-2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity=".7"/><circle cx="8" cy="4" r="1.5" fill="currentColor" opacity=".5"/></svg>
    ),
    // Auf Kurs: Kompass / gerader Strich durchs Zentrum
    aufkurs: (
      <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.2" opacity=".6"/><path d="M8 3v10M5 8h6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/><circle cx="8" cy="8" r="1" fill="currentColor"/></svg>
    ),
    // Aussenlandung: Wiese + Segler am Boden
    aussenlandung: (
      <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M2 12h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><path d="M4 12l3-2 4 1 3-1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity=".6"/><path d="M6 5l5-1 2 3-4 3z" fill="currentColor" opacity=".35"/></svg>
    ),
    // Gleiten: Segler im ruhigen Gleitflug
    gleiten: (
      <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M2 9l5-2 5 1 2-1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M7 7l.5-3M7.8 7.2l.5-2" stroke="currentColor" strokeWidth=".8" strokeLinecap="round" opacity=".5"/></svg>
    ),
    // Ueberland: Pfad + Distanz-Punkte
    ueberland: (
      <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M2 13c3-5 7-8 12-2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/><circle cx="2" cy="13" r="1.4" fill="currentColor"/><circle cx="14" cy="10.5" r="1.4" fill="currentColor"/><circle cx="8" cy="7.5" r=".9" fill="currentColor" opacity=".6"/></svg>
    ),
    // Ruppig heute: Zick-Zack turbulenz
    ruppig: (
      <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M1 10l2-3 2 2 2-4 2 5 2-3 2 2 2-1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
    ),
    // Starke Thermik: kraftvolle aufsteigende Spirale
    starkethermik: (
      <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M8 14V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><path d="M4 6l4-3 4 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><path d="M3 11c0-3 2-5 5-5s5 2 5 5" stroke="currentColor" strokeWidth="1.2" opacity=".6" strokeLinecap="round"/></svg>
    ),
    // Endanflug: Zielkreis / Approach-Richtung
    endanflug: (
      <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="5" stroke="currentColor" strokeWidth="1" opacity=".4"/><circle cx="8" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.2"/><circle cx="8" cy="8" r=".8" fill="currentColor"/><path d="M2 2l4 4M14 2l-4 4" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" opacity=".6"/></svg>
    ),
    // Abschlussbier: Bierkrug-Silhouette
    abschlussbier: (
      <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M4 6h6v7c0 .6-.4 1-1 1H5c-.6 0-1-.4-1-1z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/><path d="M10 8h2a1.5 1.5 0 010 3h-2" stroke="currentColor" strokeWidth="1.2"/><path d="M5 3l.5 1.5M7 3v1.5M9 3l-.5 1.5" stroke="currentColor" strokeWidth=".9" strokeLinecap="round" opacity=".7"/></svg>
    ),
    // Thermik: Brand — goldene aufsteigende Spirale
    thermik: (
      <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M8 14V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><path d="M5 7l3-3 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M4 12c0-2.2 1.8-4 4-4s4 1.8 4 4" stroke="currentColor" strokeWidth="1" opacity=".4" strokeLinecap="round"/><circle cx="8" cy="3" r="1.5" fill="currentColor" opacity=".6"/></svg>
    ),
    // Speed: Speedometer mit stylisiertem Zeiger
    speed: (
      <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M3 12a5 5 0 0110 0" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/><path d="M8 12l3-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><circle cx="8" cy="12" r="1.2" fill="currentColor"/></svg>
    ),
  }
  return <span style={{ color: 'var(--accent)', display: 'inline-flex' }}>{icons[theme] || null}</span>
}

const THEME_VALUES: Theme[] = [
  'start', 'schwacheraufwind', 'aufkurs', 'aussenlandung',
  'gleiten', 'ueberland', 'ruppig', 'starkethermik',
  'endanflug', 'abschlussbier', 'thermik', 'speed',
]

/**
 * SeglerToggle — kleine Pille im Header, schaltet die animierten Segler
 * im GliderBackdrop an/aus. Persistiert via localStorage 'fgl-glider-on'
 * und broadcastet via CustomEvent 'fgl-segler-toggle' an die Backdrops.
 * Mounted-Gate verhindert Hydration-Mismatch (Server rendert immer "an").
 */
function SeglerToggle() {
  const { t } = useTranslation()
  const [mounted, setMounted] = useState(false)
  const [on, setOn] = useState(true)
  useEffect(() => {
    setMounted(true)
    try {
      const v = localStorage.getItem('fgl-glider-on')
      if (v === '0') setOn(false)
    } catch { /* silent */ }
  }, [])
  function toggle() {
    const next = !on
    setOn(next)
    try { localStorage.setItem('fgl-glider-on', next ? '1' : '0') } catch { /* silent */ }
    try {
      window.dispatchEvent(new CustomEvent('fgl-segler-toggle', { detail: { on: next } }))
    } catch { /* silent */ }
  }
  if (!mounted) {
    return (
      <span
        className="inline-flex items-center justify-center h-7 w-[58px] rounded-full border border-[var(--hdr-bdr)] opacity-0"
        aria-hidden="true"
      />
    )
  }
  const titleText = on ? t('layout.seglerToggleOn') : t('layout.seglerToggleOff')
  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={on}
      aria-label={titleText}
      title={titleText}
      data-tip={titleText}
      className="wertung-tab-pill inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[10px] font-black uppercase tracking-widest shrink-0"
      data-state={on ? 'active' : 'inactive'}
    >
      <span aria-hidden style={{ fontSize: '12px', lineHeight: 1 }}>{on ? '✈' : '○'}</span>
      <span>{on ? t('layout.seglerOn') : t('layout.seglerOff')}</span>
    </button>
  )
}

/**
 * 2026-06-20: Country → Locale-Mapping für die Register-Wizard-Vorbelegung.
 * Deckt die 17 Plattform-Sprachen ab. Bei unbekanntem Land → Fallback 'en'.
 * Wird nur als Vorschlag verwendet — User kann override.
 */
function countryCodeToLocale(cc: string | null | undefined): string {
  if (!cc) return 'de'
  const c = cc.toUpperCase()
  const map: Record<string, string> = {
    DE: 'de', AT: 'de', CH: 'de', LI: 'de', LU: 'de',
    GB: 'en', IE: 'en', US: 'en', CA: 'en', AU: 'en', NZ: 'en', ZA: 'en', MT: 'en',
    FR: 'fr', MC: 'fr',
    IT: 'it', SM: 'it', VA: 'it',
    ES: 'es', AR: 'es', MX: 'es', CO: 'es', CL: 'es', PE: 'es', VE: 'es', UY: 'es', PY: 'es', BO: 'es', EC: 'es', CR: 'es', DO: 'es', GT: 'es', HN: 'es', NI: 'es', PA: 'es', SV: 'es', CU: 'es',
    PT: 'pt', BR: 'pt', AO: 'pt', MZ: 'pt',
    NL: 'nl', BE: 'nl',
    FI: 'fi',
    SE: 'sv',
    PL: 'pl',
    DK: 'da', GL: 'da', FO: 'da',
    SI: 'sl',
    JP: 'ja',
    CN: 'zh-CN', TW: 'zh-CN', HK: 'zh-CN', SG: 'zh-CN',
    KR: 'ko',
    TH: 'th',
    IL: 'he',
  }
  return map[c] || 'en'
}

export function FglHeader() {
  const { data: session, status, update: updateSession } = useSession()
  const router = useRouter()
  const pathname = usePathname()
  // R90.4 (2026-05-18) HYDRATION-SAFE: Initial IMMER 'thermik' damit SSR-HTML
  // und Client-First-Render identisch sind. localStorage erst im post-mount
  // useEffect lesen. Vorher las useState-Lazy-Init das localStorage direkt
  // → React #300 (Concurrent-Mode-Loop bei Hydration-Mismatch) auf Homepage.
  // Das Inline-Script in layout.tsx (themeScript) setzt `data-theme` schon vor
  // dem ersten React-Render aus localStorage, damit es keinen FOUC gibt.
  // Diese useState muss NUR mit dem Render synchron sein, NICHT mit dem DOM-Attribut.
  const [theme, setTheme] = useState<Theme>('thermik')
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    try {
      const saved = localStorage.getItem('fgl-theme')
      if (!saved) return
      if (THEME_VALUES.includes(saved as Theme)) {
        setTheme(saved as Theme)
        return
      }
      const migrated = LEGACY_THEME_MAP[saved]
      if (migrated) {
        localStorage.setItem('fgl-theme', migrated)
        document.documentElement.setAttribute('data-theme', migrated)
        setTheme(migrated)
      }
    } catch {}
  }, [])
  useEffect(() => setMounted(true), [])

  // 2026-05-20: mobileOpen entfernt — Mobile-Nav läuft jetzt über den EdgeRail.
  const [themeOpen, setThemeOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [showLogin, setShowLogin] = useState(false)
  const [showRegister, setShowRegister] = useState(false)
  // R102.12: Launch-Countdown im Register-Modal
  const [launchAt, setLaunchAt] = useState<string | null>(null)

  // Auto-open modals based on URL path or ?register=open / ?login=open query.
  // useSearchParams() haette eine Suspense-Boundary erfordert auf Pages die
  // sich unter /app/* statisch praerendern lassen — wir lesen daher direkt
  // window.location.search (nur clientseitig, nach Mount).
  const [loginData, setLoginData] = useState({ email: '', password: '' })
  const [registerData, setRegisterData] = useState({ firstName: '', lastName: '', displayName: '', email: '', password: '', countryCode: '' as string | null, clubName: '', dateOfBirth: '', parentalConsentEmail: '', birthdayChatOptIn: false, testerCode: '', locale: '' })

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (pathname === '/register' && !session?.user) setShowRegister(true)
    if (pathname === '/login' && !session?.user) setShowLogin(true)
    const params = new URLSearchParams(window.location.search)
    if (params.get('register') === 'open' && !session?.user) setShowRegister(true)
    if (params.get('login') === 'open' && !session?.user) setShowLogin(true)
    // Tester-Code aus ?code=FGL-XXXX-YYYY uebernehmen (Phase 2 Tester-Flow)
    const code = params.get('code')
    if (code && /^FGL-[A-Z0-9]{4}-[A-Z0-9]{4}$/i.test(code.trim())) {
      const normalized = code.trim().toUpperCase()
      setRegisterData((d) => ({ ...d, testerCode: normalized }))
    }
  }, [pathname, session])
  const [registerStep, setRegisterStep] = useState<1 | 2 | 3 | 4>(1)
  const [registerWaiverAccepted, setRegisterWaiverAccepted] = useState(false)
  const [registerTermsAccepted, setRegisterTermsAccepted] = useState(false)
  const [showRegisterWaiverText, setShowRegisterWaiverText] = useState(false)

  // Client-side Altersberechnung (Server prueft authoritativ — hier nur fuer UI-Weichen).
  // Identische Logik zu src/lib/age-verification.ts: tag-genau gegen UTC-Datum.
  const calcAge = (dob: string): number | null => {
    const m = dob.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (!m) return null
    const y = parseInt(m[1], 10), mo = parseInt(m[2], 10), da = parseInt(m[3], 10)
    const d = new Date(Date.UTC(y, mo - 1, da))
    if (d.getUTCFullYear() !== y || d.getUTCMonth() !== mo - 1 || d.getUTCDate() !== da) return null
    const now = new Date()
    if (d.getTime() > now.getTime()) return null
    let age = now.getUTCFullYear() - y
    const mDiff = now.getUTCMonth() - (mo - 1)
    const dDiff = now.getUTCDate() - da
    if (mDiff < 0 || (mDiff === 0 && dDiff < 0)) age--
    return age
  }
  const registerAge = calcAge(registerData.dateOfBirth)
  const registerAgeBracket: 'under_14' | 'parental_consent' | 'self_consent' | null =
    registerAge === null ? null : registerAge < 14 ? 'under_14' : registerAge < 16 ? 'parental_consent' : 'self_consent'
  const [authError, setAuthError] = useState<string | null>(null)
  const [authErrorDetails, setAuthErrorDetails] = useState<string[]>([])
  // 2026-06-20: Konflikt-Typ für Step-1/2 Pre-Checks. Steuert welche CTAs
  // unter der Fehlermeldung angezeigt werden (Login / Passwort vergessen / Kontakt).
  const [authConflictKind, setAuthConflictKind] = useState<null | 'klarname' | 'email'>(null)
  const [checkingAvailability, setCheckingAvailability] = useState(false)
  const [showLanguagePicker, setShowLanguagePicker] = useState(false)
  const [authLoading, setAuthLoading] = useState(false)
  const captchaConfigured = !!process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY
  const [loginCaptchaToken, setLoginCaptchaToken] = useState(captchaConfigured ? '' : 'dev-bypass')
  const [registerCaptchaToken, setRegisterCaptchaToken] = useState(captchaConfigured ? '' : 'dev-bypass')
  const [show2fa, setShow2fa] = useState(false)
  const [twoFaEmail, setTwoFaEmail] = useState('')
  const [twoFaCode, setTwoFaCode] = useState('')
  const [twoFaLoading, setTwoFaLoading] = useState(false)
  const [twoFaError, setTwoFaError] = useState<string | null>(null)
  const [showBackupInput, setShowBackupInput] = useState(false)
  const [showLoginPw, setShowLoginPw] = useState(false)
  const [showRegisterPw, setShowRegisterPw] = useState(false)

  // Body-Scroll-Lock wenn ein Modal offen ist — verhindert dass Mausrad-Events
  // durch die Modal-Schicht zum Hintergrund propagieren. CSS macht das via
  // body:has(.fgl-modal-overlay.open); dies hier ist der Fallback für ältere
  // Browser ohne :has()-Support.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined
    const anyModalOpen = showLogin || showRegister || show2fa
    if (!anyModalOpen) return undefined
    // Beide Elemente locken — je nach Browser trägt html oder body den
    // window-Scroll. Plus overscroll-behavior um Bouncing zu unterbinden.
    const html = document.documentElement
    const body = document.body
    const prevHtmlOverflow = html.style.overflow
    const prevBodyOverflow = body.style.overflow
    const prevHtmlOverscroll = html.style.overscrollBehavior
    const prevBodyOverscroll = body.style.overscrollBehavior
    html.style.overflow = 'hidden'
    body.style.overflow = 'hidden'
    html.style.overscrollBehavior = 'contain'
    body.style.overscrollBehavior = 'contain'
    return () => {
      html.style.overflow = prevHtmlOverflow
      body.style.overflow = prevBodyOverflow
      html.style.overscrollBehavior = prevHtmlOverscroll
      body.style.overscrollBehavior = prevBodyOverscroll
    }
  }, [showLogin, showRegister, show2fa])

  useEffect(() => {
    const saved = localStorage.getItem('fgl-theme') as Theme
    if (saved && THEME_VALUES.includes(saved)) {
      setTheme(saved)
      document.documentElement.setAttribute('data-theme', saved)
    } else {
      // Kein gespeichertes Theme → Thermik als Default setzen
      document.documentElement.setAttribute('data-theme', 'thermik')
      localStorage.setItem('fgl-theme', 'thermik')
    }
  }, [])

  const switchTheme = (t: Theme) => {
    setTheme(t)
    document.documentElement.setAttribute('data-theme', t)
    localStorage.setItem('fgl-theme', t)
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setAuthLoading(true)
    setAuthError(null)

    // Pre-Check: Server sagt vor dem signIn ob 2FA gebraucht wird.
    // Hintergrund: NextAuth v4 verschluckt thrown-error-messages aus authorize(),
    // darum kann signIn() das 2FA_REQUIRED-Signal nicht zuverlässig zurückgeben.
    let requires2fa = false
    try {
      const pre = await fetch('/api/auth/precheck', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginData.email, password: loginData.password }),
      })
      const preData = await pre.json().catch(() => ({}))
      if (!preData.ok) {
        setAuthError(t('auth.invalidCredentials'))
        setAuthLoading(false)
        return
      }
      requires2fa = !!preData.requires2fa
    } catch {
      setAuthError(t('common.networkError'))
      setAuthLoading(false)
      return
    }

    if (requires2fa) {
      // 2FA-Code-Dialog öffnen, signIn folgt nach Code-Verifikation in handle2faVerify
      setTwoFaEmail(loginData.email)
      setTwoFaCode('')
      setTwoFaError(null)
      setShowBackupInput(false)
      setShowLogin(false)
      setShow2fa(true)
      setAuthLoading(false)
      return
    }

    // Kein 2FA → normaler signIn
    const result = await signIn('credentials', {
      email: loginData.email, password: loginData.password, redirect: false,
    })
    if (result?.error) {
      setAuthError(result.error === 'CredentialsSignin' ? t('auth.invalidCredentials') : result.error)
    } else {
      setShowLogin(false)
      router.push('/app')
      router.refresh()
    }
    setAuthLoading(false)
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    // Pre-Submit-Validation: alle Pflichtfelder zuerst prüfen, damit der User
    // einen klaren Hinweis bekommt OHNE 400-Roundtrip zum Backend (wichtig:
    // jedes 400 zählt aufs Rate-Limit, dann kommt 429 "Zu viele Anfragen").
    if (!registerData.firstName.trim() || !registerData.lastName.trim()) {
      setAuthError(t('auth.wizErrorName'))
      return
    }
    if (!registerData.email.trim()) {
      setAuthError(t('auth.wizErrorEmail'))
      return
    }
    if (registerData.password.length < 8) {
      setAuthError(t('auth.wizErrorPassword'))
      return
    }
    if (!registerWaiverAccepted) {
      setAuthError(t('auth.wizErrorWaiver'))
      return
    }
    if (!registerTermsAccepted) {
      setAuthError(t('auth.wizErrorTerms'))
      return
    }
    if (!registerData.dateOfBirth || registerAgeBracket === null) {
      setAuthError(t('auth.errorDobRequired'))
      return
    }
    if (registerAgeBracket === 'under_14') {
      setAuthError(t('auth.errorAgeTooYoung'))
      return
    }
    if (registerAgeBracket === 'parental_consent' && !registerData.parentalConsentEmail) {
      setAuthError(t('auth.errorParentEmailRequired'))
      return
    }
    if (!registerData.countryCode) {
      setAuthError(t('auth.wizErrorCountry'))
      return
    }
    if (!registerCaptchaToken) {
      setAuthError(t('auth.wizErrorCaptcha'))
      return
    }
    setAuthLoading(true)
    setAuthError(null)
    setAuthErrorDetails([])
    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...registerData,
          // 2026-06-20: locale aus dem Wizard senden (mit Country-Fallback wenn nicht gesetzt)
          locale: registerData.locale || countryCodeToLocale(registerData.countryCode),
          // Tester-Code: nur senden wenn vorhanden — sonst wird die Public-Registrierung
          // serverseitig gegen den registration_enabled-Switch gepruefft.
          tester_code: registerData.testerCode || undefined,
          captchaToken: registerCaptchaToken,
          liability_waiver_accepted: true,
          terms_accepted: true,
        }),
      })
      const data = await res.json()
      if (res.ok) {
        if (data.verifyUrl) {
          setShowRegister(false)
          router.push(`/verify-email?dev_link=${encodeURIComponent(data.verifyUrl)}`)
        } else {
          setShowRegister(false)
          router.push('/verify-email')
        }
      } else {
        setAuthError(data.error || t('auth.registerFailed'))
        // 2026-06-20: Detail-Errors aus Backend (z.B. Passwort-Regeln) sichtbar machen
        // statt nur die generische "Passwort erfüllt nicht die Anforderungen"-Meldung.
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          setAuthErrorDetails(data.errors.filter((e: any) => typeof e === 'string'))
        } else {
          setAuthErrorDetails([])
        }
      }
    } catch { setAuthError(t('common.networkError')) }
    setAuthLoading(false)
  }

  const handle2faVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setTwoFaLoading(true)
    setTwoFaError(null)
    try {
      const res = await fetch('/api/auth/2fa/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: twoFaEmail, token: twoFaCode }),
      })
      const data = await res.json()
      if (data.success) {
        // 2FA verified — now sign in with bypass flag
        const result = await signIn('credentials', {
          email: twoFaEmail, password: loginData.password, twoFactorVerified: 'true', redirect: false,
        })
        if (result?.error) {
          setTwoFaError(result.error)
        } else {
          setShow2fa(false)
          router.push('/app')
          router.refresh()
        }
        if (data.method === 'backup') {
          // Could show remaining count but login completes
        }
      } else {
        setTwoFaError(data.error || t('twoFactor.invalidCode'))
      }
    } catch {
      setTwoFaError(t('common.networkError'))
    }
    setTwoFaLoading(false)
  }

  const openLogin = () => { setShowRegister(false); setShow2fa(false); setShowLogin(true); setAuthError(null); setLoginCaptchaToken('') }
  const openRegister = () => { setShowLogin(false); setShow2fa(false); setShowRegister(true); setAuthError(null); setRegisterCaptchaToken(''); setRegisterStep(1 as 1 | 2 | 3 | 4) }

  const { t } = useTranslation()

  const isAdminUser = (session?.user as any)?.role === 'ADMIN'
  const [isPremium, setIsPremium] = useState(false)
  const [fglWertungActive, setFglWertungActive] = useState(false)
  // 2026-05-15: 3-Stufen-Sichtbarkeit (hidden | tier_gated | public)
  // Header-Link greift bei tier_gated zusätzlich für eingeloggte User.
  const [fglWertungVisibility, setFglWertungVisibility] = useState<'hidden' | 'tier_gated' | 'public'>('hidden')
  // Public-Launch-Modus (2026-05-09): Login + Register-Buttons im Header sind
  // standardmaessig versteckt. Stephan oeffnet das Login-Modal via:
  //   - 5x Klick aufs FGL-Logo innerhalb 3 Sekunden, ODER
  //   - Strg+Shift+L (Tastatur-Shortcut)
  // Tester registrieren sich via /register?code=FGL-XXXX-YYYY (auto-open Modal).
  // Admin kann beide Buttons jederzeit per /admin/platform-Toggle einschalten.
  const [showLoginButton, setShowLoginButton] = useState(false)
  const [showRegisterButton, setShowRegisterButton] = useState(false)
  // Admin-Toggle ob freie Registrierung moeglich ist. Steuert sichtbarkeit
  // des „Jetzt registrieren"-Links im Login-Modal. Default false bis API antwortet,
  // damit der Link bei deaktivierter Registrierung nicht kurz aufflackert.
  const [registrationEnabled, setRegistrationEnabled] = useState(false)

  // ── 2FA-Onboarding-Status nach Login holen ──
  const [twoFaEnabled, setTwoFaEnabled] = useState<boolean | null>(null)
  const [twoFaFirstName, setTwoFaFirstName] = useState<string | null>(null)
  const [twoFaModalOpen, setTwoFaModalOpen] = useState(false)
  // R90.2 (2026-05-18) HYDRATION-SAFE: initial IMMER false. sessionStorage-Read
  // im post-mount useEffect (sonst SSR=false vs Client=true Hydration-Mismatch
  // = React Error #300 "Cannot update component while rendering different one").
  const [twoFaUserDismissed, setTwoFaUserDismissed] = useState<boolean>(false)
  useEffect(() => {
    try {
      if (sessionStorage.getItem('fgl_2fa_dismissed_this_session') === '1') {
        setTwoFaUserDismissed(true)
      }
    } catch {}
  }, [])
  const twoFaStatusKnown = twoFaEnabled !== null
  // 2026-06-18 abends (Stephan-Spec, Punkt 7): nullable durchreichen, damit der
  // Hook solange show=false zwingen kann bis der Server-Status bekannt ist.
  // Vorher: `twoFaEnabled === true` ergab `null → false` und ließ den Hook
  // unterscheiden zwischen "unbekannt" und "definitiv nicht aktiviert" nicht —
  // Modal flackerte kurz auf während des Status-Fetches.
  const twoFaTrigger = useTwoFactorOnboardingTrigger(
    twoFaEnabled,
    isAdminUser,
  )
  useEffect(() => {
    if (!session?.user) { setTwoFaEnabled(null); setTwoFaModalOpen(false); return }
    fetch('/api/auth/2fa/status')
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d && typeof d.enabled === 'boolean') setTwoFaEnabled(d.enabled)
      })
      .catch(() => {})
    // Vorname aus dem Session-Namen extrahieren — nur für die persönliche Anrede
    const name = session.user.name as string | undefined
    if (name) setTwoFaFirstName(name.trim().split(/\s+/)[0] || null)
  }, [session])
  // Modal-Open synchron zu (trigger.show && !userDismissed). Beide Richtungen
  // werden aktualisiert — sonst bleibt das Modal offen wenn show später false
  // wird (z.B. nach erfolgreichem 2FA-Setup oder DB-Bypass).
  useEffect(() => {
    if (!twoFaStatusKnown) return
    setTwoFaModalOpen(!twoFaUserDismissed && twoFaTrigger.show)
  }, [twoFaStatusKnown, twoFaTrigger.show, twoFaUserDismissed])
  // R89.2 (2026-05-18) BFCache-Workaround: Browser-Back stellt React-State
  // aus Cache wieder her. Wenn das Modal in der vorigen Page-Sicht offen war,
  // poppt es nach Back-Navigation wieder hoch — selbst wenn der User es
  // gerade dismissed hatte. sessionStorage neu lesen nach pageshow(persisted).
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return
      try {
        const dismissed = sessionStorage.getItem('fgl_2fa_dismissed_this_session') === '1'
        setTwoFaUserDismissed(dismissed)
        if (dismissed) setTwoFaModalOpen(false)
      } catch {}
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [])
  useEffect(() => {
    if (!session?.user || isAdminUser) return
    fetch('/api/subscription').then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.planTier === 'premium') setIsPremium(true) })
      .catch(() => {})
  }, [session, isAdminUser])
  useEffect(() => {
    fetch('/api/public/site-state')
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.fglWertungActive) setFglWertungActive(true)
        const v = d?.fglWertungVisibility
        if (v === 'public' || v === 'tier_gated' || v === 'hidden') setFglWertungVisibility(v)
        if (d?.showLoginButton) setShowLoginButton(true)
        if (d?.showRegisterButton) setShowRegisterButton(true)
        // registrationEnabled default false bis API antwortet (keine flackernde Link-Anzeige)
        setRegistrationEnabled(d?.registrationEnabled === true)
        // R102.12: Launch-Countdown im Register-Modal anzeigen
        if (typeof d?.launchAt === 'string') setLaunchAt(d.launchAt)
      })
      .catch(() => {})
  }, [])

  // Verstecktes Login-Trigger: 5x Logo-Klick innerhalb 3s + Strg+Shift+L (analog UC-Seite)
  // Funktioniert IMMER, auch wenn der Login-Button ausgeblendet ist.
  const logoClickTimesRef = useRef<number[]>([])
  function handleLogoSecretClick() {
    const now = Date.now()
    const times = logoClickTimesRef.current
    times.push(now)
    // Nur die letzten 3 Sekunden behalten
    while (times.length && now - times[0] > 3000) times.shift()
    if (times.length >= 5) {
      logoClickTimesRef.current = []
      if (!session?.user) {
        setShowRegister(false)
        setShow2fa(false)
        setShowLogin(true)
      }
    }
  }
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.ctrlKey && e.shiftKey && (e.key === 'L' || e.key === 'l')) {
        e.preventDefault()
        if (!session?.user) {
          setShowRegister(false)
          setShow2fa(false)
          setShowLogin(true)
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [session])
  const isPremiumOrAdmin = isAdminUser || isPremium

  // R90.5 (2026-05-18): NAV_LINKS in useMemo — vorher wurde das Array bei
  // JEDEM Header-Render neu erzeugt (jedes Item neue Objekt-Referenz), was bei
  // Multi-State-Renders (3 parallele fetch-Setter aus site-state + 2fa-status
  // + subscription) zu mehrfachen Re-Render-Pässen der Link-Children führte.
  // Mit stabiler Array-Referenz bei unveränderten Deps bleibt der Render-Tree
  // ruhig — bricht den Trigger-Pfad für React #300 nach Login.
  // 2026-05-20: Icon-Pills — kompaktes Lucide-Icon + Kurz-Label statt Caps-
  // Vollwort. Full-Label bleibt im title-Attribut für Hover-Tooltip + A11y.
  const NAV_LINKS = useMemo(() => {
    const userLoggedIn = !!session?.user
    return [
      { href: '/news',          short: t('nav.newsShort'),          full: t('nav.news'),            icon: Newspaper },
      // R100.44 (2026-05-28): Wertung-Link IMMER im Menü. Wenn die Wertung
      // nicht public ist, zeigt /wertung selbst eine klare Login-Aufforderung.
      // Wenn 'hidden' und kein Admin → /wertung zeigt eine "Saison bereitet sich vor"-Notiz.
      { href: '/wertung',  short: t('nav.rankingsShort'),  full: t('nav.rankings'),     icon: Trophy },
      // r19.43 (2026-06-24, Stephan-Spec): FGL-Modell-Park öffentlich im Menü.
      // Liste aller in der FGL genutzten Basismodelle mit Σ-Aggregaten
      // (Maschinen, Piloten, Stunden, km) — Pilot-Identitäten nur eingeloggt.
      { href: '/aircraft-models', short: t('nav.aircraftModelsShort'),  full: t('nav.aircraftModels'),  icon: Plane },
      // 2026-06-22 Stephan-Spec: Podcast (Daniel Wönicker auf mo-pilot.com)
      // im Hauptmenü. Page pullt RSS-Feed mit Redis-Cache, neue Folgen
      // erscheinen max. 6 h nach Veröffentlichung automatisch.
      { href: '/podcast',  short: t('nav.podcastShort'),  full: t('nav.podcast'),             icon: Mic },
      { href: '/features',      short: t('nav.featuresShort'), full: t('nav.features'),     icon: Sparkles },
      { href: '/flying-fields', short: t('nav.flyingFieldsShort'),   full: t('nav.flyingFields'), icon: MapPin },
      { href: '/weather',       short: t('nav.weatherShort'),   full: t('nav.weather'),      icon: Cloud },
      // 2026-06-15 abend: Heatmap aus Public-Nav entfernt — Nebel-Render-Bug
      // hinter Cesium-Globus. Page bleibt via Direkt-URL /heatmap erreichbar,
      // ist nur nicht mehr in Navigation gelistet. Wartet auf Fix in TODO H1.
      ...(userLoggedIn ? [{ href: '/airspace-3d', short: t('nav.airspace3dShort'), full: t('nav.airspace3d'), icon: Box }] : []),
      { href: '/contacts',      short: t('nav.contactsShort'), full: t('nav.contacts'),     icon: Users },
      { href: '/mitmachen',     short: t('nav.pricingShort'),full: t('nav.pricing'),      icon: Rocket },
      { href: '/donate',        short: t('nav.supportShort'), full: t('nav.support'), icon: Heart },
      { href: '/faq',           short: t('nav.faqShort'),      full: t('nav.faq'),          icon: HelpCircle },
    ]
  }, [t, fglWertungVisibility, session?.user?.id, isAdminUser, isPremiumOrAdmin])

  return (
    <>
      {/* ═══ ZEILE 1 — Logo + Theme + Auth ═══ */}
      <header className="fgl-header">
        {/* 2026-05-20: AppSidebar-Trigger entfernt — Mobile-Nav läuft jetzt
             komplett über den EdgeRail unten. Auf Desktop ist die AppSidebar
             permanent links sichtbar. */}

        {/* Logo — auch versteckter Login-Trigger (5x klicken in 3s oeffnet Login-Modal) */}
        <Link href="/" className="flex items-center gap-3 shrink-0" data-tip="FREE GLIDE LEAGUE" onClick={handleLogoSecretClick}>
          <div className="fgl-logo-frame w-10 h-10" style={{ fontSize: 40, boxShadow: '0 0 8px color-mix(in oklch, var(--accent) 15%, transparent)' }}>
            <FglLogoSvg aria-label="FGL" />
          </div>
          <span className="font-black text-[15px] tracking-[.10em] uppercase whitespace-nowrap hidden sm:inline" style={{ color: 'var(--accent)', textShadow: '0 0 12px color-mix(in oklch, var(--accent) 20%, transparent)' }}>
            FREE GLIDE LEAGUE
          </span>
        </Link>

        <div className="flex-1 flex items-center justify-center">
          {/* Live-Online-Piloten-Counter (2026-05-20) — eingeloggte User
               schicken alle 30 s einen Heartbeat, alle pollen den Count.
               Pulsierender grüner Punkt + "X online". Auf Mobile nur Zahl. */}
          <OnlineCounter />
        </div>

        {/* Theme Switcher — kompakt */}
        <div className="relative shrink-0">
          <button
            onClick={() => setThemeOpen(!themeOpen)}
            className="flex items-center gap-1.5 bg-[var(--pill-bg)] border border-[var(--border)] text-[var(--text)] text-[10px] font-bold tracking-[.04em] rounded-lg px-2.5 py-1.5 cursor-pointer hover:border-[var(--accent)] transition-colors"
          >
            {mounted && <ThemeIcon theme={theme} />}
            <span className="hidden sm:inline" suppressHydrationWarning>
              {mounted ? t(`theme.${theme}`) : ''}
            </span>
            <span className="text-[var(--muted)] text-[9px]">▼</span>
          </button>
          {themeOpen && (
            <div className="absolute end-0 top-full mt-1 z-50 min-w-[160px] rounded-xl border border-[var(--border)] bg-[var(--bg-card)] shadow-lg overflow-hidden">
              {THEME_VALUES.map((tv) => (
                <button
                  key={tv}
                  onClick={() => { switchTheme(tv); setThemeOpen(false) }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-[11px] font-bold tracking-[.04em] hover:bg-[var(--accent)]/10 transition-colors text-start ${theme === tv ? 'bg-[var(--accent)]/15 text-[var(--accent)]' : 'text-[var(--text)]'}`}
                >
                  {mounted && <ThemeIcon theme={tv} />}
                  <span>{t(`theme.${tv}`)}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Segler-Toggle Pille (R100.51 2026-05-27): wandert aus dem
            fixierten Bottom-Right des GliderBackdrop in den Header.
            Synchronisierung über localStorage + CustomEvent SEGLER_EVENT. */}
        <SeglerToggle />

        {/* R102 (P15 + P19, 2026-05-31): Alarmglocke (logged-in mit Unread-Badge)
            + Chatbot-Quick-Aufruf (Cirra public / Freeglidy logged-in). */}
        <HeaderQuickIcons />

        {/* Language Switcher */}
        <LanguageSwitcher />

        <div className="w-px h-5 bg-[var(--hdr-bdr)] shrink-0 hidden sm:block" />

        {/* Auth — kompakt (Reload-Icon entfernt R100.51 2026-05-27,
            Service-Worker-Reset weiterhin über /reset-sw.html erreichbar) */}
        <div className="flex gap-1.5 items-center shrink-0">
          {status === 'loading' ? (
            <div className="w-16 h-7 bg-[var(--pill-bg)] rounded-full animate-pulse" />
          ) : session?.user ? (
            <>
              {/* 2026-05-20 Header-Cleanup v2: kein FlightProgressBar mehr, dafür
                  ein einziger User-Menü-Trigger der auf JEDER Auflösung Dashboard,
                  Einstellungen und Abmelden anbietet. Stephan-Wunsch:
                  „es betrifft alle auflösungen". */}
              {session.user.role === 'ADMIN' && (
                <Link href="/admin" className="btn-secondary !px-2.5 !py-1.5 !text-[10px] !text-[var(--accent)]">Admin</Link>
              )}
              <div className="relative shrink-0">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="inline-flex items-center gap-1.5 bg-[var(--pill-bg)] border border-[var(--border)] text-[var(--text)] text-[10px] font-bold tracking-[.04em] rounded-lg px-2.5 py-1.5 cursor-pointer hover:border-[var(--accent)] transition-colors"
                  aria-label="User-Menü"
                  title={session.user.email || 'Account'}
                  data-tip={session.user.email || 'Account'}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  <span className="text-[var(--muted)] text-[9px]">▼</span>
                </button>
                {userMenuOpen && (
                  <>
                    {/* Click-Outside-Closer */}
                    <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                    <div className="absolute end-0 top-full mt-1 z-50 min-w-[200px] rounded-xl border border-[var(--border)] bg-[var(--bg-card)] shadow-lg overflow-hidden">
                      <div className="px-3 py-2 border-b border-[var(--border)]">
                        <div className="text-[11px] font-black text-[var(--text)] truncate">{session.user.name || 'Pilot'}</div>
                        <div className="text-[10px] text-[var(--muted)] truncate">{session.user.email}</div>
                      </div>
                      <Link
                        href="/app/settings"
                        onClick={() => setUserMenuOpen(false)}
                        className="block w-full px-3 py-2 text-[11px] font-bold tracking-[.04em] hover:bg-[var(--accent)]/10 text-[var(--text)]"
                      >
                        Einstellungen
                      </Link>
                      <button
                        onClick={() => { setUserMenuOpen(false); signOut({ callbackUrl: '/' }) }}
                        className="block w-full px-3 py-2 text-[11px] font-bold tracking-[.04em] hover:bg-[var(--accent)]/10 text-start text-[var(--text)]"
                      >
                        {t('nav.logout')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <>
              {/* R103.4 (2026-06-04, Stephan-Wunsch T+1 nach Launch):
                  Login + Register als EIN Button "MITMACHEN" zusammengefasst.
                  Klick oeffnet Login-Modal mit Tab-Switcher (Login / Registrieren)
                  oben drueber. Default-Tab = Login (haeufiger gebraucht). Mobile-
                  friendly (1 Button statt 2). Login bleibt fuer Stephan via
                  Strg+Shift+L oder 5x Logo-Klick auch ohne Button erreichbar. */}
              {(showLoginButton || showRegisterButton) && (
                <button
                  onClick={showLoginButton ? openLogin : openRegister}
                  className="btn-primary !px-3 !py-1.5 !text-[11px] inline-flex items-center gap-1.5"
                  aria-label={t('auth.joinIn')}
                  data-tip={t('auth.joinIn')}
                >
                  {/* R106.4.4 (Stephan-Spec 2026-06-08): Mobile reduziert auf
                      reines Login-Icon (Platzsparen im Mobile-Header), Tablet+
                      bleibt der Text-Button "MITMACHEN". */}
                  <LogIn className="h-4 w-4 shrink-0" strokeWidth={2.4} />
                  <span className="hidden sm:inline">{t('auth.joinIn')}</span>
                </button>
              )}
            </>
          )}
        </div>

        {/* 2026-05-20: Mobile-Burger entfernt — EdgeRail übernimmt. */}
      </header>

      {/* ═══ ZEILE 2 — Navigation (Icon-Pills, kompakt) ═══ */}
      <nav className="hidden md:block sticky top-[48px] z-[199] border-b border-[var(--hdr-bdr)]"
        style={{ background: 'var(--hdr-bg)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }}>
        <div className="flex items-center gap-1 px-4 h-[36px] overflow-x-auto scrollbar-none max-w-[1400px] mx-auto">
          {NAV_LINKS.map((l) => {
            const isActive = pathname === l.href || (l.href !== '/' && pathname.startsWith(l.href))
            const Icon = l.icon
            return (
              <Link
                key={l.href}
                href={l.href}
                title={l.full}
                data-tip={l.full}
                aria-label={l.full}
                className={`inline-flex items-center gap-1.5 text-[11px] font-extrabold tracking-[.04em] no-underline px-2.5 py-1.5 rounded-lg transition-all whitespace-nowrap shrink-0 ${
                  isActive
                    ? 'bg-[var(--accent)]/10 border border-[var(--accent)]/25'
                    : 'hover:bg-[var(--accent)]/5 border border-transparent'
                }`}
                style={{
                  color: isActive ? 'var(--nav-active-color, var(--accent))' : 'var(--hdr-muted)',
                }}
              >
                <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={2.2} />
                <span>{l.short}</span>
              </Link>
            )
          })}
        </div>
      </nav>

      {/* R103.31 (T-Day, Stephan-Spec): Tagesticker mit den letzten 20 frisch
          eingereichten Wertungsfluegen. Glass-Look, Marquee, auf jeder
          oeffentlichen Seite sichtbar. Klick fuehrt zur Zeitwertung. */}
      <RecentFlightsTicker />

      {/* 2026-05-20: Mobile-Nav-Overlay entfernt — Mobile-Nav liegt jetzt im
           EdgeRail (Bottom-Sheet via FGL-Anchor-Puck). */}

      {/* ═══ LOGIN MODAL ═══ */}
      <div className={`fgl-modal-overlay ${showLogin ? 'open' : ''}`} data-lenis-prevent onMouseDown={(e) => { if (e.target === e.currentTarget) setShowLogin(false) }}>
        <div className="fgl-modal" data-lenis-prevent onMouseDown={(e) => e.stopPropagation()}>
          <button onClick={() => setShowLogin(false)} className="absolute top-3.5 end-3.5 text-[var(--muted)] text-xl w-8 h-8 rounded-lg flex items-center justify-center hover:bg-[var(--pill-bg)] hover:text-[var(--text)] cursor-pointer border-none bg-transparent transition-colors">✕</button>
          <div className="fgl-logo-frame w-14 h-14 mx-auto mb-4" style={{ fontSize: 56 }}>
            <FglLogoSvg aria-label="FGL" />
          </div>
          {/* R103.4 (2026-06-04): Tab-Switcher Login/Registrieren. Aktiver Tab
              farbig hinterlegt, Klick auf inaktiven Tab oeffnet das andere Modal
              (openLogin/openRegister schliessen das eigene + oeffnen das Ziel). */}
          {showRegisterButton && (
            <div className="flex items-center gap-2 mx-auto mb-4 max-w-[320px]">
              <button
                type="button"
                aria-current="page"
                data-state="active"
                className="wertung-tab-pill wertung-tab-pill-lg flex-1 px-4 py-2.5 rounded-full text-[12px] font-black uppercase tracking-[.04em] cursor-default"
              >
                {t('auth.loginTitle')}
              </button>
              <button
                type="button"
                onClick={openRegister}
                data-state="inactive"
                className="wertung-tab-pill wertung-tab-pill-lg flex-1 px-4 py-2.5 rounded-full text-[12px] font-black uppercase tracking-[.04em] cursor-pointer"
              >
                {t('auth.registerTitle')}
              </button>
            </div>
          )}
          <h2 className="text-[22px] font-black tracking-[.04em] uppercase text-center mb-1.5">{t('auth.loginTitle')}</h2>
          <p className="text-center text-[13px] text-[var(--muted)] mb-6 font-bold">{t('auth.loginWelcome')} ✦</p>
          {authError && (
            <div
              className="mb-4 p-3 rounded-xl text-sm font-bold"
              style={{
                background: 'var(--state-danger-soft)',
                border: '1px solid var(--state-danger-bdr)',
                color: 'var(--state-danger)',
              }}
            >
              {authError}
            </div>
          )}
          <form onSubmit={handleLogin}>
            <div className="mb-3.5">
              <label className="block text-[11px] font-black tracking-[.10em] uppercase text-[var(--muted)] mb-1.5">{t('common.email')}</label>
              <input type="email" value={loginData.email} onChange={(e) => setLoginData(d => ({ ...d, email: e.target.value }))} placeholder={t('auth.emailPlaceholder')} className="input" required />
            </div>
            <div className="mb-3.5">
              <label className="block text-[11px] font-black tracking-[.10em] uppercase text-[var(--muted)] mb-1.5">{t('common.password')}</label>
              <div className="relative">
                <input type={showLoginPw ? 'text' : 'password'} value={loginData.password} onChange={(e) => setLoginData(d => ({ ...d, password: e.target.value }))} placeholder="••••••••" className="input !pe-10" required />
                <button type="button" onClick={() => setShowLoginPw(v => !v)} className="absolute end-3 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-[var(--accent)] bg-transparent border-none cursor-pointer p-0" tabIndex={-1}>
                  {showLoginPw ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  )}
                </button>
              </div>
            </div>
            <button type="submit" disabled={authLoading} className="btn-primary w-full !py-3.5 mt-1.5">{authLoading ? t('auth.loggingIn') : `▶ ${t('auth.loginButton')}`}</button>
            <div className="text-center mt-3">
              <Link
                href="/reset-password"
                onClick={() => setShowLogin(false)}
                className="text-[11px] text-[var(--muted)] hover:text-[var(--accent)] underline-offset-2 hover:underline transition-colors"
              >
                {t('auth.forgotPassword')}
              </Link>
            </div>
          </form>
          {/* Register-Link nur wenn Admin-Toggle „registration_enabled" aktiv ist.
              Bei deaktivierter Registrierung (Default ab 2026-05-09 vor Public-Launch
              03.06.2026) bekommt der Login-Modal-Besucher einen Hinweis statt eines
              klickbaren Links — Tester-Phase laeuft ueber /register?code=FGL-XXXX-YYYY. */}
          {registrationEnabled ? (
            <p className="text-center text-[12px] text-[var(--muted)] mt-4 font-bold">
              {t('auth.noAccount')} <button onClick={openRegister} className="text-[var(--link-col)] font-black cursor-pointer bg-transparent border-none underline-offset-2 hover:underline">{t('auth.registerNow')} →</button>
            </p>
          ) : (
            <p className="text-center text-[11px] text-[var(--muted)] mt-4 leading-relaxed">
              Die offene Registrierung ist derzeit deaktiviert.
              <br />
              Bist du Tester? Nutze deinen FGL-Code aus der Einladungsmail.
            </p>
          )}
        </div>
      </div>

      {/* ═══ REGISTER MODAL ═══ */}
      <div className={`fgl-modal-overlay ${showRegister ? 'open' : ''}`} data-lenis-prevent onMouseDown={(e) => { if (e.target === e.currentTarget) setShowRegister(false) }}>
        <div className="fgl-modal" data-lenis-prevent onMouseDown={(e) => e.stopPropagation()}>
          <button onClick={() => setShowRegister(false)} className="absolute top-3.5 end-3.5 text-[var(--muted)] text-xl w-8 h-8 rounded-lg flex items-center justify-center hover:bg-[var(--pill-bg)] hover:text-[var(--text)] cursor-pointer border-none bg-transparent transition-colors">✕</button>
          <div className="fgl-logo-frame w-14 h-14 mx-auto mb-4" style={{ fontSize: 56 }}>
            <FglLogoSvg aria-label="FGL" />
          </div>
          {/* R103.4 (2026-06-04): Tab-Switcher Login/Registrieren — Login-Tab
              wechselt zurueck zum Login-Modal. Tester-Code-Pfad bleibt unangetastet. */}
          {showLoginButton && !registerData.testerCode && (
            <div className="flex items-center gap-2 mx-auto mb-4 max-w-[320px]">
              <button
                type="button"
                onClick={openLogin}
                data-state="inactive"
                className="wertung-tab-pill wertung-tab-pill-lg flex-1 px-4 py-2.5 rounded-full text-[12px] font-black uppercase tracking-[.04em] cursor-pointer"
              >
                {t('auth.loginTitle')}
              </button>
              <button
                type="button"
                aria-current="page"
                data-state="active"
                className="wertung-tab-pill wertung-tab-pill-lg flex-1 px-4 py-2.5 rounded-full text-[12px] font-black uppercase tracking-[.04em] cursor-default"
              >
                {t('auth.registerTitle')}
              </button>
            </div>
          )}
          {/* Registrierung deaktiviert (Admin-Toggle): Hinweis-Card statt Wizard.
              Tester mit Code registrieren weiter (registerData.testerCode != ''). */}
          {!registrationEnabled && !registerData.testerCode ? (
            <div className="text-center py-6">
              <h2 className="text-[22px] font-black tracking-[.04em] uppercase mb-3">{t('auth.wizDisabledTitle')}</h2>
              <p className="text-[13px] text-[var(--muted)] mb-3 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('auth.wizDisabledBody1') }} />
              <p className="text-[13px] text-[var(--muted)] mb-5 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('auth.wizDisabledBody2') }} />
              <p className="text-[12px] text-[var(--muted)] mb-5" dangerouslySetInnerHTML={{ __html: t('auth.wizDisabledBody3') }} />
              <button
                onClick={() => setShowRegister(false)}
                className="btn-primary"
                data-tip={t('auth.wizDisabledOkTip')}
              >
                {t('auth.wizDisabledOk')}
              </button>
            </div>
          ) : registerData.testerCode ? (
            <>
              <div
                className="mx-auto mb-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[10px] font-black tracking-[.12em] uppercase"
                style={{
                  background: 'color-mix(in oklch, var(--accent) 15%, transparent)',
                  color: 'var(--accent)',
                  border: '1px solid var(--accent)',
                  boxShadow: '0 0 16px var(--accent)',
                  display: 'flex',
                  width: 'fit-content',
                }}
                data-tip={t('auth.wizTesterBadgeTip')}
              >
                {t('auth.wizTesterBadge')}
              </div>
              <h2 className="text-[22px] font-black tracking-[.04em] uppercase text-center mb-1.5">{t('auth.wizTesterTitle')}</h2>
              <p className="text-center text-[12px] mb-1 font-mono" style={{ color: 'var(--accent)' }}>
                {t('auth.wizTesterCodeLabel')}: <strong>{registerData.testerCode}</strong>
              </p>
              <p className="text-center text-[12px] text-[var(--muted)] mb-4 font-bold">
                {t('auth.wizTesterThanks')}
              </p>
            </>
          ) : (
            <>
              <h2 className="text-[22px] font-black tracking-[.04em] uppercase text-center mb-1.5">{t('auth.registerTitle')}</h2>
              <p className="text-center text-[13px] text-[var(--muted)] mb-3 font-bold">{t('auth.registerSubtitle')} ✦</p>
              {/* R102.12 (2026-06-02, Stephan-Anweisung Pre-Launch T-2):
                  Countdown bis zur offenen Registrierung. Nach Ablauf blendet
                  LaunchCountdown sich selbst aus (return null) — Stephan
                  schaltet die Registrierung dann manuell als Admin frei. */}
              {launchAt && (
                <div className="flex justify-center mb-4">
                  <LaunchCountdown launchAt={launchAt} variant="inline" />
                </div>
              )}
            </>
          )}

          {authError && (
            <div
              className="mb-4 p-4 rounded-xl text-sm font-bold"
              style={{
                background: 'var(--state-danger-soft)',
                border: '2px solid var(--state-danger-bdr)',
                color: 'var(--state-danger)',
                boxShadow: '0 4px 20px -4px color-mix(in oklch, var(--state-danger) 35%, transparent)',
              }}
            >
              <div className="flex items-start gap-2 mb-1">
                <span aria-hidden className="text-xl leading-none mt-0.5">⚠</span>
                <span className="flex-1">{authError}</span>
              </div>
              {authErrorDetails.length > 0 && (
                <ul className="mt-2 pl-7 list-disc space-y-1 text-[13px] font-semibold" style={{ color: 'var(--state-danger)' }}>
                  {authErrorDetails.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
              {/* 2026-06-20: Konflikt-CTAs — theme-aware via --accent + --state-danger */}
              {authConflictKind === 'email' && (
                <div className="mt-3 pl-7 flex flex-wrap gap-2 text-[12px] font-bold">
                  <button
                    type="button"
                    onClick={() => { setShowRegister(false); setLoginData(d => ({ ...d, email: registerData.email })); setShowLogin(true) }}
                    className="px-3 py-1.5 rounded-lg hover:opacity-90 transition-opacity"
                    style={{ background: 'var(--accent)', color: 'var(--accent-contrast)' }}
                    data-tip={t('auth.wizConflictEmailLoginTip')}
                  >
                    {t('auth.wizConflictEmailLogin')}
                  </button>
                  <a
                    href="/reset-password"
                    onClick={() => setShowRegister(false)}
                    className="px-3 py-1.5 rounded-lg hover:opacity-90 transition-opacity no-underline"
                    style={{
                      border: '1px solid var(--state-danger-bdr)',
                      color: 'var(--state-danger)',
                      background: 'var(--state-danger-soft)',
                    }}
                    data-tip={t('auth.wizConflictForgotPwTip')}
                  >
                    {t('auth.forgotPassword')}
                  </a>
                </div>
              )}
              {authConflictKind === 'klarname' && (
                <div className="mt-3 pl-7 flex flex-wrap gap-2 text-[12px] font-bold">
                  <button
                    type="button"
                    onClick={() => { setShowRegister(false); setShowLogin(true) }}
                    className="px-3 py-1.5 rounded-lg hover:opacity-90 transition-opacity"
                    style={{ background: 'var(--accent)', color: 'var(--accent-contrast)' }}
                    data-tip={t('auth.wizConflictKlarnameMineTip')}
                  >
                    {t('auth.wizConflictKlarnameMine')}
                  </button>
                  <a
                    href={`mailto:info@free-glide-league.com?subject=${encodeURIComponent(t('auth.wizConflictKlarnameMailSubject'))}&body=${encodeURIComponent(t('auth.wizConflictKlarnameMailBody', { firstName: registerData.firstName, lastName: registerData.lastName }))}`}
                    className="px-3 py-1.5 rounded-lg hover:opacity-90 transition-opacity no-underline"
                    style={{
                      border: '1px solid var(--state-danger-bdr)',
                      color: 'var(--state-danger)',
                      background: 'var(--state-danger-soft)',
                    }}
                    data-tip={t('auth.wizConflictKlarnameOtherTip')}
                  >
                    {t('auth.wizConflictKlarnameOther')}
                  </a>
                </div>
              )}
            </div>
          )}
          {/* ─── Stepper-Header (4 Steps, kompakt — nur Zahlen + Active-Label) ──── */}
          {(() => {
            // 2026-05-15: displayName (Spitzname) ist optional — nur Vor- + Nachname Pflicht.
            const step1Valid = !!registerData.firstName.trim() && !!registerData.lastName.trim()
            const step2Valid = !!registerData.email && registerData.password.length >= 8
            const step3Valid = !!registerData.countryCode
            const step4AgeValid = registerAgeBracket === 'self_consent' || (registerAgeBracket === 'parental_consent' && !!registerData.parentalConsentEmail.trim())
            const step4Valid = registerWaiverAccepted && registerTermsAccepted && step4AgeValid && !!registerCaptchaToken
            const stepValid = [step1Valid, step2Valid, step3Valid, step4Valid]
            const labels = [t('auth.wizStepName'), t('auth.wizStepAccess'), t('auth.wizStepLocation'), t('auth.wizStepConsent')]
            return (
              <div className="flex items-center justify-between mb-4 px-1">
                {[1, 2, 3, 4].map((n, idx) => {
                  const isActive = registerStep === n
                  const isDone = stepValid[n - 1]
                  return (
                    <div key={n} className="flex items-center flex-1 min-w-0">
                      <button
                        type="button"
                        onClick={() => { if (n < registerStep || isDone) setRegisterStep(n as 1 | 2 | 3 | 4) }}
                        className={`flex items-center gap-1.5 cursor-pointer bg-transparent border-none shrink-0 ${isActive ? 'text-[var(--accent)]' : isDone ? 'text-[var(--text)]' : 'text-[var(--muted)]'}`}
                        title={labels[n - 1]}
                        data-tip={labels[n - 1]}
                      >
                        <span className={`flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-black ${isActive ? 'bg-[var(--accent)] text-[var(--bg-primary)]' : isDone ? 'bg-[var(--accent)]/30 text-[var(--accent)]' : 'bg-[var(--bg-secondary)] text-[var(--muted)]'}`}>
                          {isDone && !isActive ? '✓' : n}
                        </span>
                        {isActive && <span className="text-[11px] font-black tracking-[.08em] uppercase">{labels[n - 1]}</span>}
                      </button>
                      {idx < 3 && <div className={`flex-1 h-px mx-2 ${isDone ? 'bg-[var(--accent)]/40' : 'bg-[var(--border)]'}`} />}
                    </div>
                  )
                })}
              </div>
            )
          })()}

          <form onSubmit={handleRegister}>
            {/* ─── Schritt 1: Name ─────────────────────────────── */}
            {registerStep === 1 && (
              <>
                {/* R102 (Stephan-Update 2026-05-31): präzisierter Text — keine
                    Erwähnung von Echtname-Sichtbarkeit unter eingeloggten Piloten,
                    nur klar dass ohne Echtname kein Ranglisten-Eintrag. */}
                <div
                  className="mb-4 p-3 rounded-xl border"
                  style={{
                    background: 'color-mix(in oklch, var(--accent) 10%, transparent)',
                    borderColor: 'color-mix(in oklch, var(--accent) 35%, transparent)',
                  }}
                >
                  <p className="text-[13px] font-black mb-1" style={{ color: 'var(--accent)' }}>
                    {t('auth.wizStep1TitleBold')}
                  </p>
                  <p className="text-[11px] leading-relaxed" style={{ color: 'var(--text)' }}>
                    {t('auth.wizStep1Body')}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2.5 mb-3">
                  <div>
                    <label className="block text-[11px] font-black tracking-[.10em] uppercase text-[var(--muted)] mb-1.5">{t('auth.wizFirstName')} *</label>
                    {/* R106.4.8 (Stephan-Bug 2026-06-08): autoFocus raus —
                        ploppt Mobile-Tastatur auf bei jedem Seitenwechsel. */}
                    <input type="text" value={registerData.firstName} onChange={(e) => setRegisterData(d => ({ ...d, firstName: e.target.value }))} placeholder={t('auth.wizFirstNamePh')} className="input" required data-tip={t('auth.wizFirstNameTip')} />
                  </div>
                  <div>
                    <label className="block text-[11px] font-black tracking-[.10em] uppercase text-[var(--muted)] mb-1.5">{t('auth.wizLastName')} *</label>
                    <input type="text" value={registerData.lastName} onChange={(e) => setRegisterData(d => ({ ...d, lastName: e.target.value }))} placeholder={t('auth.wizLastNamePh')} className="input" required data-tip={t('auth.wizLastNameTip')} />
                  </div>
                </div>
                <p className="text-[10px] text-[var(--muted)] mb-3 opacity-80 leading-snug" dangerouslySetInnerHTML={{ __html: t('auth.wizStep1CorrectionHint') }} />
                <div className="mb-1">
                  <label className="block text-[11px] font-black tracking-[.10em] uppercase text-[var(--muted)] mb-1.5">{t('auth.wizDisplayName')}</label>
                  <input type="text" value={registerData.displayName} onChange={(e) => setRegisterData(d => ({ ...d, displayName: e.target.value }))} placeholder={t('auth.wizDisplayNamePh')} className="input" data-tip={t('auth.wizDisplayNameTip')} />
                </div>
              </>
            )}

            {/* ─── Schritt 2: Zugangsdaten ─────────────────────── */}
            {registerStep === 2 && (
              <>
                <p className="text-[11px] text-[var(--muted)] mb-3 leading-relaxed">{t('auth.wizStep2Body')}</p>
                <div className="mb-3">
                  <label className="block text-[11px] font-black tracking-[.10em] uppercase text-[var(--muted)] mb-1.5">{t('common.email')} *</label>
                  {/* R106.4.8: autoFocus raus — Mobile-Tastatur-Trigger */}
                  <input type="email" value={registerData.email} onChange={(e) => setRegisterData(d => ({ ...d, email: e.target.value }))} placeholder={t('auth.emailPlaceholder')} className="input" required />
                </div>
                <div className="mb-2">
                  <label className="block text-[11px] font-black tracking-[.10em] uppercase text-[var(--muted)] mb-1.5">{t('common.password')} *</label>
                  <div className="relative">
                    <input type={showRegisterPw ? 'text' : 'password'} value={registerData.password} onChange={(e) => setRegisterData(d => ({ ...d, password: e.target.value }))} placeholder={t('auth.passwordPlaceholder')} className="input !pe-10" required minLength={8} />
                    <button type="button" onClick={() => setShowRegisterPw(v => !v)} className="absolute end-3 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-[var(--accent)] bg-transparent border-none cursor-pointer p-0" tabIndex={-1}>
                      {showRegisterPw ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      )}
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const chars = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%&*'
                    const pw = Array.from(crypto.getRandomValues(new Uint8Array(16))).map(b => chars[b % chars.length]).join('')
                    setRegisterData(d => ({ ...d, password: pw }))
                    setShowRegisterPw(true)
                  }}
                  className="mb-3 w-full py-1.5 px-3 rounded-lg border border-[var(--border)] bg-[var(--bg-secondary)] text-[10px] font-bold text-[var(--accent)] hover:bg-[var(--accent)]/10 hover:border-[var(--accent)]/40 transition-colors flex items-center justify-center gap-2"
                  data-tip={t('auth.wizGenPasswordTip')}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                  {t('auth.wizGenPassword')}
                </button>
                <details className="rounded-[10px] border border-[var(--border)] bg-[var(--bg-secondary)]/30 overflow-hidden group">
                  <summary className="cursor-pointer list-none px-3 py-2 flex items-center justify-between text-[11px] font-black tracking-[.08em] uppercase text-[var(--muted)] hover:text-[var(--accent)] select-none">
                    <span>{t('auth.wizSecTips')}</span>
                    <span className="transition-transform group-open:rotate-90">▸</span>
                  </summary>
                  <div className="px-3 pb-3 pt-1 space-y-2">
                    <p className="text-[10px] leading-relaxed text-[var(--muted)]" dangerouslySetInnerHTML={{ __html: t('auth.wizSecTip1') }} />
                    <p className="text-[10px] leading-relaxed text-[var(--accent)] font-bold" dangerouslySetInnerHTML={{ __html: t('auth.wizSecTip2') }} />
                  </div>
                </details>
              </>
            )}

            {/* ─── Schritt 3: Standort ─────────────────────────── */}
            {registerStep === 3 && (
              <>
                <p className="text-[11px] text-[var(--muted)] mb-3 leading-relaxed">{t('auth.wizStep3Body')}</p>
                <div className="mb-3">
                  <label className="block text-[11px] font-black tracking-[.10em] uppercase text-[var(--muted)] mb-1.5">{t('auth.wizCountry')} *</label>
                  <CountryCombobox
                    value={registerData.countryCode}
                    onChange={(code) => {
                      // 2026-06-20: Land-Auswahl belegt Sprache automatisch vor —
                      // nur wenn Sprache noch leer war (User hat sie noch nicht
                      // selbst geändert), damit Override nicht überschrieben wird.
                      setRegisterData(d => ({
                        ...d,
                        countryCode: code,
                        locale: d.locale ? d.locale : countryCodeToLocale(code),
                      }))
                    }}
                    required
                    placeholder={t('auth.wizCountryPh')}
                  />
                </div>
                {/* Sprache: kompakt anzeigen mit Auto-Vorbelegung aus Land,
                    Reveal-Button für Override-Picker (2026-06-20) */}
                {(() => {
                  const effLocale = registerData.locale || (registerData.countryCode ? countryCodeToLocale(registerData.countryCode) : 'de')
                  const localeLabel: Record<string, string> = {
                    de: '🇩🇪 Deutsch', en: '🇬🇧 English', fr: '🇫🇷 Français', it: '🇮🇹 Italiano',
                    es: '🇪🇸 Español', pt: '🇵🇹 Português', nl: '🇳🇱 Nederlands', fi: '🇫🇮 Suomi',
                    sv: '🇸🇪 Svenska', pl: '🇵🇱 Polski', da: '🇩🇰 Dansk', sl: '🇸🇮 Slovenščina',
                    ja: '🇯🇵 日本語', 'zh-CN': '🇨🇳 中文', ko: '🇰🇷 한국어', th: '🇹🇭 ไทย', he: '🇮🇱 עברית',
                  }
                  return (
                    <div className="mb-3">
                      <label className="block text-[11px] font-black tracking-[.10em] uppercase text-[var(--muted)] mb-1.5">{t('auth.wizLanguage')}</label>
                      <div className="rounded-[10px] border border-[var(--border)] bg-[var(--bg-secondary)]/30 p-2.5">
                        <div className="flex items-center justify-between gap-2 text-[13px]">
                          <span className="font-bold">{localeLabel[effLocale] || effLocale}</span>
                          {!showLanguagePicker && (
                            <button
                              type="button"
                              onClick={() => setShowLanguagePicker(true)}
                              className="text-[10px] font-bold tracking-wider uppercase text-[var(--accent)] hover:underline bg-transparent border-none cursor-pointer p-0"
                              data-tip={t('auth.wizLanguageOverrideTip')}
                            >
                              {t('auth.wizLanguageOverride')}
                            </button>
                          )}
                        </div>
                        {showLanguagePicker && (
                          <select
                            value={effLocale}
                            onChange={(e) => setRegisterData(d => ({ ...d, locale: e.target.value }))}
                            className="input mt-2"
                            autoFocus
                          >
                            <option value="de">🇩🇪 Deutsch</option>
                            <option value="en">🇬🇧 English</option>
                            <option value="fr">🇫🇷 Français</option>
                            <option value="it">🇮🇹 Italiano</option>
                            <option value="es">🇪🇸 Español</option>
                            <option value="pt">🇵🇹 Português</option>
                            <option value="nl">🇳🇱 Nederlands</option>
                            <option value="fi">🇫🇮 Suomi</option>
                            <option value="sv">🇸🇪 Svenska</option>
                            <option value="pl">🇵🇱 Polski</option>
                            <option value="da">🇩🇰 Dansk</option>
                            <option value="sl">🇸🇮 Slovenščina</option>
                            <option value="ja">🇯🇵 日本語</option>
                            <option value="zh-CN">🇨🇳 中文</option>
                            <option value="ko">🇰🇷 한국어</option>
                            <option value="th">🇹🇭 ไทย</option>
                            <option value="he">🇮🇱 עברית</option>
                          </select>
                        )}
                        <p className="text-[9px] text-[var(--muted)] mt-1.5 opacity-70">{t('auth.wizLanguageHint')}</p>
                      </div>
                    </div>
                  )
                })()}
                <div className="mb-3">
                  <label className="block text-[11px] font-black tracking-[.10em] uppercase text-[var(--muted)] mb-1.5">{t('auth.wizClub')} <span className="text-[var(--muted)] font-normal normal-case">({t('auth.wizOptional')})</span></label>
                  <input
                    type="text"
                    value={registerData.clubName}
                    onChange={(e) => setRegisterData(d => ({ ...d, clubName: e.target.value.slice(0, 100) }))}
                    placeholder={t('auth.wizClubPh')}
                    className="input"
                    maxLength={100}
                    data-tip={t('auth.wizClubTip')}
                  />
                  <p className="text-[9px] text-[var(--muted)] mt-1 opacity-70">{t('auth.wizClubHint')}</p>
                </div>
              </>
            )}

            {/* ─── Schritt 4: Zustimmung ───────────────────────── */}
            {registerStep === 4 && (
              <div className="space-y-2.5">
                {/* DSGVO Art. 13 — Kurze Zweckmitteilung sichtbar, bevor Einwilligung erfolgt */}
                <div
                  className="text-[10px] leading-relaxed text-[var(--muted)] bg-[var(--bg-secondary)]/30 border border-[var(--border)] rounded-[8px] p-2.5"
                  dangerouslySetInnerHTML={{ __html: t('auth.wizDataPurpose') }}
                />

                {/* Zustimmung 1: AGB + Wettbewerbsordnung + Datenschutz (Art. 7 DSGVO) */}
                <label className="flex items-start gap-2 cursor-pointer p-2.5 rounded-[10px] border border-[var(--accent)]/30 bg-[var(--accent)]/5" data-tip={t('auth.wizTermsTip')}>
                  <input type="checkbox" checked={registerTermsAccepted} onChange={e => setRegisterTermsAccepted(e.target.checked)} className="mt-1 w-4 h-4 accent-[var(--accent)] flex-shrink-0" />
                  <span
                    className="text-[11px] text-[var(--text)] leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: t('auth.wizTermsLabel') }}
                  />
                </label>

                {/* Zustimmung 2: Haftungsausschluss (spezifische Einwilligung) */}
                <label className="flex items-start gap-2 cursor-pointer p-2.5 rounded-[10px] border border-[var(--accent)]/30 bg-[var(--accent)]/5" data-tip={t('auth.wizWaiverTip')}>
                  <input type="checkbox" checked={registerWaiverAccepted} onChange={e => setRegisterWaiverAccepted(e.target.checked)} className="mt-1 w-4 h-4 accent-[var(--accent)] flex-shrink-0" />
                  <span className="text-[11px] text-[var(--text)] leading-relaxed">
                    <span dangerouslySetInnerHTML={{ __html: t('auth.wizWaiverLabel') }} />{' '}
                    <button type="button" onClick={(e) => { e.preventDefault(); setShowRegisterWaiverText(s => !s) }} className="text-[var(--accent)] underline bg-transparent border-none p-0 cursor-pointer">
                      {showRegisterWaiverText ? t('auth.wizWaiverHide') : t('auth.wizWaiverShow')}
                    </button>
                  </span>
                </label>
                {showRegisterWaiverText && (
                  <pre className="whitespace-pre-wrap text-[10px] leading-relaxed text-[var(--text)] font-sans bg-[var(--bg-primary)] p-3 rounded-[8px] max-h-[200px] overflow-y-auto">
                    {t('auth.wizWaiverFullText')}
                  </pre>
                )}

                {/* Altersverifikation (Art. 8 DSGVO + BGB §§ 107 ff.) — tag-genaues Geburtsdatum */}
                <div className="p-2.5 rounded-[10px] border border-[var(--accent)]/30 bg-[var(--accent)]/5">
                  <label className="block text-[11px] font-bold text-[var(--text)] mb-1.5">
                    {t('auth.dobLabel')} <span className="text-[var(--accent)]">*</span>
                  </label>
                  <input
                    type="date"
                    value={registerData.dateOfBirth}
                    onChange={e => setRegisterData(d => ({ ...d, dateOfBirth: e.target.value, parentalConsentEmail: '' }))}
                    max={new Date().toISOString().split('T')[0]}
                    className="input"
                    required
                  />
                  {!registerData.dateOfBirth && (
                    <p className="mt-1 text-[10px] font-bold" style={{ color: 'var(--state-danger)' }}>
                      {t('auth.dobRequiredInline')}
                    </p>
                  )}
                  <p className="text-[9px] text-[var(--muted)] mt-1 opacity-70">{t('auth.dobHint')}</p>

                  {/* Unter 14 — Block-Screen */}
                  {registerAgeBracket === 'under_14' && (
                    <div
                      className="mt-2 p-2.5 rounded-[8px] border text-[11px] leading-relaxed"
                      style={{
                        background: 'var(--state-danger-soft)',
                        borderColor: 'var(--state-danger-bdr)',
                        color: 'var(--state-danger)',
                      }}
                    >
                      <strong>{t('auth.ageUnder14Title')}</strong> {t('auth.ageUnder14Body')}
                    </div>
                  )}

                  {/* 14–16 — Eltern-E-Mail erforderlich */}
                  {registerAgeBracket === 'parental_consent' && (
                    <div className="mt-2 space-y-2">
                      <div
                        className="p-2.5 rounded-[8px] border text-[11px] leading-relaxed text-[var(--text)]"
                        style={{
                          background: 'var(--state-warning-soft)',
                          borderColor: 'var(--state-warning-bdr)',
                        }}
                      >
                        <strong>{t('auth.parentalConsentTitle')}</strong> {t('auth.parentalConsentBody')}
                      </div>
                      <label className="block text-[11px] font-bold text-[var(--text)] mb-1">
                        {t('auth.parentalEmailLabel')} <span className="text-[var(--accent)]">*</span>
                      </label>
                      <input
                        type="email"
                        value={registerData.parentalConsentEmail}
                        onChange={e => setRegisterData(d => ({ ...d, parentalConsentEmail: e.target.value }))}
                        placeholder="eltern@example.com"
                        className="input"
                        required
                      />
                      <p className="text-[9px] text-[var(--muted)] opacity-70">{t('auth.parentalEmailHint')}</p>
                    </div>
                  )}

                  {/* ≥ 16 — Bestätigung erfolgt implizit ueber das Datum */}
                  {registerAgeBracket === 'self_consent' && (
                    <p className="mt-1.5 text-[10px] text-[var(--muted)] opacity-80">{t('auth.ageSelfConsentOk')}</p>
                  )}
                </div>

                {/* Optional: Geburtstagschat-Opt-In (Art. 6 Abs. 1 lit. a DSGVO, freiwillig) */}
                {(registerAgeBracket === 'self_consent' || registerAgeBracket === 'parental_consent') && (
                  <label className="flex items-start gap-2 cursor-pointer p-2.5 rounded-[10px] border border-[var(--border)] bg-[var(--bg-secondary)]/30">
                    <input type="checkbox" checked={registerData.birthdayChatOptIn} onChange={e => setRegisterData(d => ({ ...d, birthdayChatOptIn: e.target.checked }))} className="mt-1 w-4 h-4 accent-[var(--accent)] flex-shrink-0" />
                    <span className="text-[11px] text-[var(--text)] leading-relaxed">
                      <strong>{t('auth.birthdayChatOptInPrefix')}</strong> {t('auth.birthdayChatOptInLabel')}
                    </span>
                  </label>
                )}

                <Turnstile onVerify={setRegisterCaptchaToken} className="mt-1" />
              </div>
            )}

            {/* ─── Wizard-Navigation ──────────────────────────── */}
            <div className="flex gap-2 mt-4">
              {registerStep > 1 && (
                <button
                  type="button"
                  onClick={() => setRegisterStep(s => (s - 1) as 1 | 2 | 3 | 4)}
                  className="flex-1 py-3 px-4 rounded-[14px] border border-[var(--border)] bg-[var(--bg-secondary)] text-[12px] font-black tracking-[.06em] uppercase text-[var(--text)] hover:bg-[var(--bg-primary)] transition-colors"
                  data-tip={t('auth.wizBackTip')}
                >
                  {t('auth.wizBack')}
                </button>
              )}
              {registerStep < 4 && (
                <button
                  type="button"
                  disabled={checkingAvailability}
                  onClick={async (e) => {
                    e.preventDefault()
                    setAuthErrorDetails([])
                    setAuthConflictKind(null)
                    if (registerStep === 1) {
                      const fn = registerData.firstName.trim()
                      const ln = registerData.lastName.trim()
                      if (!fn || !ln) {
                        setAuthError(t('auth.wizErrorName'))
                        return
                      }
                      // 2026-06-20: Backend Pre-Check ob Klarname bereits registriert ist
                      setCheckingAvailability(true)
                      try {
                        const res = await fetch('/api/auth/check-availability', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ type: 'klarname', firstName: fn, lastName: ln }),
                        })
                        const data = await res.json()
                        if (res.ok && data.available === false) {
                          setAuthError(data.message || t('auth.wizErrorKlarnameTaken', { fullName: `${fn} ${ln}` }))
                          setAuthConflictKind('klarname')
                          setCheckingAvailability(false)
                          return
                        }
                      } catch { /* Network-Fehler ignorieren, Backend-Check beim Submit greift trotzdem */ }
                      setCheckingAvailability(false)
                    }
                    if (registerStep === 2) {
                      if (!registerData.email || registerData.password.length < 8) {
                        setAuthError(t('auth.wizErrorAccess'))
                        return
                      }
                      // 2026-06-20: Backend Pre-Check ob E-Mail bereits registriert ist
                      setCheckingAvailability(true)
                      try {
                        const res = await fetch('/api/auth/check-availability', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ type: 'email', email: registerData.email.trim() }),
                        })
                        const data = await res.json()
                        if (res.ok && data.available === false) {
                          setAuthError(data.message || t('auth.wizErrorEmailTaken'))
                          setAuthConflictKind('email')
                          setCheckingAvailability(false)
                          return
                        }
                      } catch { /* Network-Fehler ignorieren */ }
                      setCheckingAvailability(false)
                    }
                    if (registerStep === 3 && !registerData.countryCode) {
                      setAuthError(t('auth.wizErrorCountry'))
                      return
                    }
                    setAuthError(null)
                    setAuthErrorDetails([])
                    setAuthConflictKind(null)
                    setRegisterStep(s => (s + 1) as 1 | 2 | 3 | 4)
                  }}
                  className="btn-primary flex-1 !py-3 text-[12px] disabled:opacity-60"
                  data-tip={t('auth.wizNextTip')}
                >
                  {checkingAvailability ? t('auth.wizChecking') : t('auth.wizNext')}
                </button>
              )}
              {registerStep === 4 && (
                <button
                  type="submit"
                  disabled={authLoading || !registerCaptchaToken || !registerWaiverAccepted || !registerTermsAccepted || !(registerAgeBracket === 'self_consent' || (registerAgeBracket === 'parental_consent' && !!registerData.parentalConsentEmail.trim()))}
                  className="btn-primary flex-1 !py-3 text-[12px]"
                >
                  {authLoading ? t('auth.registering') : `✦ ${t('auth.registerButton')}`}
                </button>
              )}
            </div>
          </form>
          <p className="text-center text-[12px] text-[var(--muted)] mt-4 font-bold">
            {t('auth.alreadyRegistered')} <button onClick={openLogin} className="text-[var(--link-col)] font-black cursor-pointer bg-transparent border-none underline-offset-2 hover:underline">{t('auth.loginNow')} →</button>
          </p>
        </div>
      </div>

      {/* ═══ 2FA VERIFICATION MODAL ═══ */}
      <div className={`fgl-modal-overlay ${show2fa ? 'open' : ''}`} onClick={(e) => { if (e.target === e.currentTarget) { setShow2fa(false); openLogin() } }}>
        <div className="fgl-modal">
          <button onClick={() => { setShow2fa(false); openLogin() }} className="absolute top-3.5 end-3.5 text-[var(--muted)] text-xl w-8 h-8 rounded-lg flex items-center justify-center hover:bg-[var(--pill-bg)] hover:text-[var(--text)] cursor-pointer border-none bg-transparent transition-colors">✕</button>
          <div className="w-14 h-14 rounded-[14px] border border-[var(--border)] bg-[var(--pill-bg)] mx-auto mb-4 flex items-center justify-center">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
            </svg>
          </div>
          <h2 className="text-[22px] font-black tracking-[.04em] uppercase text-center mb-1.5">{t('twoFactor.title')}</h2>
          <p className="text-center text-[13px] text-[var(--muted)] mb-6 font-bold">{t('twoFactor.verifyLogin')}</p>
          {twoFaError && (
            <div
              className="mb-4 p-3 rounded-xl text-sm font-bold"
              style={{
                background: 'var(--state-danger-soft)',
                border: '1px solid var(--state-danger-bdr)',
                color: 'var(--state-danger)',
              }}
            >
              {twoFaError}
            </div>
          )}
          <form onSubmit={handle2faVerify}>
            <div className="mb-4">
              <label className="block text-[11px] font-black tracking-[.10em] uppercase text-[var(--muted)] mb-1.5">
                {showBackupInput ? t('twoFactor.backupCodes') : t('twoFactor.codePlaceholder')}
              </label>
              <input
                type="text"
                value={twoFaCode}
                onChange={(e) => setTwoFaCode(showBackupInput ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder={showBackupInput ? 'XXXXXXXX' : '000000'}
                className="input text-center text-[24px] font-mono tracking-[.3em]"
                maxLength={showBackupInput ? 8 : 6}
                /* R106.4.8: autoFocus raus — ploppt Mobile-Tastatur beim Mount */
                autoComplete="one-time-code"
                inputMode={showBackupInput ? 'text' : 'numeric'}
                required
              />
            </div>
            <button
              type="submit"
              disabled={twoFaLoading || (showBackupInput ? twoFaCode.length < 8 : twoFaCode.length < 6)}
              className="btn-primary w-full !py-3.5 mt-1.5"
            >
              {twoFaLoading ? t('common.loading') : t('twoFactor.verify')}
            </button>
          </form>
          <button
            onClick={() => { setShowBackupInput(!showBackupInput); setTwoFaCode(''); setTwoFaError(null) }}
            className="block mx-auto mt-4 text-[12px] text-[var(--link-col)] font-bold cursor-pointer bg-transparent border-none hover:underline"
          >
            {showBackupInput ? t('twoFactor.codePlaceholder') : t('twoFactor.useBackupCode')}
          </button>
        </div>
      </div>

      {/* ═══ 2FA ONBOARDING MODAL — nach Login, wenn 2FA noch nicht aktiv ═══ */}
      {twoFaStatusKnown && (
        <TwoFactorOnboardingModal
          open={twoFaModalOpen}
          forceSetup={twoFaTrigger.force}
          firstName={twoFaFirstName}
          onClose={() => {
            // User hat aktiv geschlossen — Session-weit dismissed-Flag setzen,
            // damit das Modal nicht beim nächsten Render/Remount erneut aufpoppt.
            setTwoFaUserDismissed(true)
            setTwoFaModalOpen(false)
          }}
          onSuccess={() => {
            setTwoFaEnabled(true)
            setTwoFaUserDismissed(false)
            try { sessionStorage.removeItem('fgl_2fa_dismissed_this_session') } catch {}
            setTwoFaModalOpen(false)
            // JWT mit frischem two_factor_enabled-Flag refreshen,
            // damit Admin-Middleware ihn sofort durchlässt.
            updateSession?.()
          }}
        />
      )}
    </>
  )
}
